import axios from 'axios';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { atom, selector } from 'recoil';
import { WorkflowModesID } from 'src/products/data-submission-portal/__generated__/constants';

interface TokenResponse {
  token: string;
  baseUrl: string;
}

interface HttpsHasuraIoJwtClaims {
  'x-hasura-default-role': string;
  'x-hasura-allowed-roles': string[];
  'x-hasura-allowed-workflow-mode'?: string;
  'x-hasura-workflow-mode'?: string;
  'x-hasura-user-id': string;
  'x-hasura-org-id': string;
}

export interface DecodedToken extends JwtPayload {
  'https://hasura.io/jwt/claims': HttpsHasuraIoJwtClaims;
}

const hasuraAuthUrl = '/api/v1/dsp/hasura-token';

export class DSPHasuraJWT {
  private static _instance: DSPHasuraJWT | null = null;
  private setAtom: ((token: DecodedToken) => void) | null = null;
  private encodedToken: string | null = null;
  private decodedToken: DecodedToken | null = null;
  private baseUrl: URL | null = null;
  private fetchingToken: Promise<void> | null = null;
  private autoFetchHasuraTokenTimeoutId: ReturnType<typeof setTimeout> | null =
    null;
  private mode: WorkflowModesID | null = null;

  public static getInstance(): DSPHasuraJWT {
    if (!DSPHasuraJWT._instance) {
      DSPHasuraJWT._instance = new DSPHasuraJWT();
    }
    return DSPHasuraJWT._instance;
  }

  public atom = atom<DecodedToken | null>({
    key: 'DSPHasuraJWT/decodedToken',
    effects: [
      (params) => {
        this.setAtom = (token: DecodedToken) => {
          params.setSelf(token);
        };

        if (params.trigger === 'get') {
          if (!this.mode) {
            params.setSelf(new Promise(() => {}));
          } else if (!this.decodedToken || this.isExpired()) {
            params.setSelf(new Promise(() => {}));
            this.queueFetchHasuraToken();
          } else {
            params.setSelf(this.decodedToken);
          }
        }
      },
    ],
  });

  public selector = selector({
    key: 'DSPHasuraJWT/rolesSelector',
    get: ({ get }) => {
      const token = get(this.atom);
      if (token instanceof Promise) {
        throw token;
      }
      return (
        token?.['https://hasura.io/jwt/claims']['x-hasura-allowed-roles'] || []
      );
    },
  });

  private isExpired() {
    if (!this.decodedToken) {
      return true;
    }
    const now = Date.now() / 1000;
    return this.decodedToken.exp ? this.decodedToken.exp < now : false;
  }

  public async queueFetchHasuraToken(force = false) {
    if (!this.isExpired() && this.decodedToken && !force) {
      return;
    }

    if (!this.fetchingToken) {
      this.fetchingToken = this.fetchHasuraToken()
        .then(() => {})
        .finally(() => {
          this.fetchingToken = null;
        });
    }

    await this.fetchingToken;
  }

  private async fetchHasuraToken() {
    if (!this.mode) {
      throw new Error('Mode is not set, you must call setMode first');
    }

    this.decodedToken = null;
    this.encodedToken = null;

    const res = await axios.get<TokenResponse>(hasuraAuthUrl, {
      params: {
        mode: this.mode,
      },
    });

    const { token, baseUrl } = res.data;
    this.baseUrl = new URL(baseUrl);
    this.encodedToken = token;

    try {
      this.decodedToken = jwtDecode<DecodedToken>(token);
    } catch (e) {
      throw new Error('Could not decode Hasura token');
    }

    this.setAtom?.(this.decodedToken);

    if (this.decodedToken.exp) {
      // clear timeout if it exists
      if (this.autoFetchHasuraTokenTimeoutId) {
        clearTimeout(this.autoFetchHasuraTokenTimeoutId);
      }

      // set timeout to refresh token
      const gracePeriod = 60; // seconds
      const subtrahend = Date.now() / 1000 + gracePeriod;
      const delayInSeconds = Math.max(this.decodedToken.exp - subtrahend, 0); // handle negative values

      this.autoFetchHasuraTokenTimeoutId = setTimeout(
        () => this.queueFetchHasuraToken(true),
        delayInSeconds * 1000
      );
    }
  }

  public getBaseUrl() {
    if (!this.baseUrl) {
      throw new Error(
        'Base URL is not set, you must call queueFetchHasuraToken first'
      );
    }
    return this.baseUrl;
  }

  public getEncodedToken() {
    if (!this.encodedToken) {
      throw new Error(
        'Encoded token is not set, you must call queueFetchHasuraToken first'
      );
    }

    return this.encodedToken;
  }

  public getDecodedToken() {
    if (!this.decodedToken) {
      throw new Error(
        'Decoded token is not set, you must call queueFetchHasuraToken first'
      );
    }
    return this.decodedToken;
  }

  public async setMode(mode: WorkflowModesID) {
    if (
      this.mode === mode ||
      (this.decodedToken &&
        this.decodedToken['https://hasura.io/jwt/claims'][
          'x-hasura-workflow-mode'
        ] === mode.toString())
    ) {
      return;
    }
    this.mode = mode;
    await this.queueFetchHasuraToken(true);
  }
}
